import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Certificate from "@/components/Certificate";
import VideoPlayer from "@/components/VideoPlayer";
import { triggerConfetti } from "@/utils/confetti";
import { playCompletionSound } from "@/utils/sounds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CompletionPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [videoWatched, setVideoWatched] = useState(false);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  const checkCompletion = async () => {
    try {
      const progressRes = await axios.get(`${API}/progress/me`);
      const { total_lessons, completed_lessons } = progressRes.data;
      
      // If not actually completed, redirect to dashboard
      if (completed_lessons < total_lessons) {
        navigate("/dashboard");
        return;
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Error checking completion:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Trigger celebration on mount
    triggerConfetti();
    playCompletionSound();
    checkCompletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVideoEnd = () => {
    setVideoWatched(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-100">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Congratulations Header */}
        <div className="text-center mb-8">
          <div className="text-8xl mb-4 animate-bounce">🎉</div>
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            Congratulations, {user.name}!
          </h1>
          <p className="text-2xl text-gray-700 mb-2">
            You&apos;ve completed the Lemonade Stand Business Plan Program!
          </p>
          <p className="text-xl text-gray-600">
            You&apos;re now ready to launch your entrepreneurial journey! 🍋
          </p>
        </div>

        {/* Achievement Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center transform hover:scale-105 transition-transform">
            <div className="text-5xl mb-2">📚</div>
            <div className="text-4xl font-bold text-yellow-600">21</div>
            <div className="text-gray-600 font-semibold">Lessons Completed</div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center transform hover:scale-105 transition-transform">
            <div className="text-5xl mb-2">🏆</div>
            <div className="text-4xl font-bold text-green-600">{user.points || 0}</div>
            <div className="text-gray-600 font-semibold">Points Earned</div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center transform hover:scale-105 transition-transform">
            <div className="text-5xl mb-2">⭐</div>
            <div className="text-4xl font-bold text-blue-600">{user.badges?.length || 0}</div>
            <div className="text-gray-600 font-semibold">Badges Earned</div>
          </div>
        </div>

        {/* Closing Video */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              🎬 {t('completion.whatComesNext', 'What Comes Next?')}
            </h2>
            <p className="text-gray-600">
              {t('completion.watchSpecialMessage', 'Watch this special message about your entrepreneurial future!')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <VideoPlayer
              src="https://customer-assets.emergentagent.com/job_learn-lemons/artifacts/viu81vg1_What%20Comes%20Next_.mp4"
              onEnded={handleVideoEnd}
              poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='450'%3E%3Crect width='800' height='450' fill='%23fef3c7'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23f59e0b' font-size='48' font-family='Arial'%3E▶ Play Video%3C/text%3E%3C/svg%3E"
              videoRef={videoRef}
            />
          </div>

          {videoWatched && (
            <div className="text-center mt-6">
              <div className="inline-block bg-green-100 text-green-700 px-6 py-3 rounded-full font-semibold">
                ✓ {t('completion.videoWatchedAllSet', "Video Watched - You're All Set!")}
              </div>
            </div>
          )}
        </div>

        {/* Certificate Section */}
        <div className="mb-8">
          <Certificate user={user} />
        </div>

        {/* Business Workbook Section (Reflections) */}
        <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="text-center">
            <div className="text-6xl mb-4">📓</div>
            <h2 className="text-3xl font-bold mb-3">
              📓 My Business Workbook
            </h2>
            <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
              Review all your answers and reflections from each session. 
              This is your personal record of what you learned throughout the program!
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate("/workbook")}
                className="flex items-center gap-2 px-6 py-3 bg-white text-green-600 rounded-xl font-bold hover:bg-gray-100 transition-colors shadow-lg"
                data-testid="view-workbook-btn"
              >
                <span className="text-xl">📖</span>
                View My Workbook
              </button>
              <button
                onClick={() => {
                  navigate("/workbook");
                }}
                className="flex items-center gap-2 px-6 py-3 bg-white/20 text-white rounded-xl font-bold hover:bg-white/30 transition-colors border-2 border-white/40"
                data-testid="download-workbook-btn"
              >
                <span className="text-xl">📥</span>
                Download PDF
              </button>
            </div>
            <p className="text-sm opacity-70 mt-4">
              Contains your raw answers from all 7 sessions
            </p>
          </div>
        </div>

        {/* AI Business Plan Section */}
        <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-500 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 left-8 text-8xl">🍋</div>
            <div className="absolute bottom-4 right-8 text-8xl">💼</div>
          </div>
          <div className="relative text-center">
            <div className="text-6xl mb-4">📋</div>
            <h2 className="text-3xl font-bold mb-3" style={{textShadow: "1px 1px 2px rgba(0,0,0,0.2)"}}>
              🚀 My Business Plan
            </h2>
            <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
              Your actionable, launch-ready business plan! LemonBot takes your ideas and creates 
              a real plan with prices, profit math, and a launch day checklist.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate("/ai-business-plan")}
                className="flex items-center gap-2 px-6 py-3 bg-white text-orange-600 rounded-xl font-bold hover:bg-gray-100 transition-colors shadow-lg"
                data-testid="view-business-plan-btn"
              >
                <span className="text-xl">📋</span>
                View My Business Plan
              </button>
              <button
                onClick={() => navigate("/pitch-practice")}
                className="flex items-center gap-2 px-6 py-3 bg-white/20 text-white rounded-xl font-bold hover:bg-white/30 transition-colors border-2 border-white/40"
                data-testid="use-for-pitch-btn"
              >
                <span className="text-xl">🎤</span>
                Practice My Pitch
              </button>
            </div>
            
            <p className="text-sm opacity-80 mt-4">
              ✨ AI-powered • Actionable steps • Real prices & profit math
            </p>
          </div>
        </div>

        {/* Earned Badges */}
        {user.badges && user.badges.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              🎖️ Your Earned Badges
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {user.badges.map((badge, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-yellow-100 to-orange-100 border-2 border-yellow-400 rounded-xl p-4 text-center transform hover:scale-110 transition-transform"
                >
                  <div className="text-4xl mb-2">⭐</div>
                  <p className="text-sm font-semibold text-gray-800 capitalize">
                    {badge.replace(/_/g, ' ')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center">
          <button
            onClick={() => navigate("/dashboard")}
            className="lemon-button px-8 py-4 rounded-xl text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompletionPage;
